import React, { useState, useEffect } from "react";
import { api } from "../../lib/apiClient";
import { Edit3, Plus, Trash2 } from "lucide-react";
import { Input, SimpleDialog } from "./_shared";
import { toast } from "../../components/ui/sonner";
import { useConfirm } from "../../components/ConfirmDialog";

export function MarkupsTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const confirm = useConfirm();

  const load = () => api.get("/markup-options").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    const pct = Number(editing.percent);
    if (!Number.isFinite(pct) || pct < 0) return toast.error("Percent must be a non-negative number");
    try {
      if (editing.id) await api.put(`/markup-options/${editing.id}`, { percent: pct, label: editing.label || "" });
      else await api.post("/markup-options", { percent: pct, label: editing.label || "" });
      setEditing(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    }
  };
  const remove = async (id) => {
    const ok = await confirm({ title: "Delete this markup option?", confirmText: "Delete", destructive: true });
    if (!ok) return;
    await api.delete(`/markup-options/${id}`);
    load();
  };

  return (
    <div className="space-y-4" data-testid="markups-tab">
      <div className="text-xs text-slate-500 max-w-2xl">
        Define the markup percentages that estimators can pick from in the Parts &amp; Materials and Sundries tables.
        Add or remove options here to control the available choices everywhere.
      </div>
      <button
        onClick={() => setEditing({ percent: 0, label: "" })}
        className="h-9 px-4 bg-brand-blue text-white text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-2 hover:bg-brand-blue-dark"
        data-testid="add-markup-button"
      >
        <Plus size={14} /> Add Markup
      </button>
      <div className="border border-slate-200">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b-2 border-slate-900">
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-600">Percent</th>
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-600">Label (optional)</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={3} className="px-3 py-6 text-center text-slate-400 text-sm">No markup options yet.</td></tr>}
            {items.map((m) => (
              <tr key={m.id} className="border-b border-slate-200 hover:bg-slate-50">
                <td className="px-3 py-2 font-mono font-semibold">{m.percent}%</td>
                <td className="px-3 py-2 text-slate-600">{m.label || <span className="text-slate-400 italic">—</span>}</td>
                <td className="px-3 py-2 text-right space-x-2">
                  <button onClick={() => setEditing({ id: m.id, percent: m.percent, label: m.label || "" })} className="text-slate-500 hover:text-slate-900"><Edit3 size={14} /></button>
                  <button onClick={() => remove(m.id)} className="text-slate-400 hover:text-red-600"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && (
        <SimpleDialog title={editing.id ? "Edit Markup" : "New Markup"} onClose={() => setEditing(null)} onSave={save}>
          <Input label="Percent (%)" type="number" value={editing.percent} set={(v) => setEditing({ ...editing, percent: v })} />
          <Input label="Label (optional)" value={editing.label} set={(v) => setEditing({ ...editing, label: v })} />
        </SimpleDialog>
      )}
    </div>
  );
}
